import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TemplateRef,
} from "@angular/core";
import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import { CommonModule } from "@angular/common";
import { BehaviorSubject, combineLatest, Subject, debounceTime } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { ButtonDirective } from "@components";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "app-swiper-wrapper",
  standalone: true,
  imports: [CommonModule, FaIconComponent, ButtonDirective, TranslateModule],
  templateUrl: "./swiper-wrapper.component.html",
  styleUrl: "./swiper-wrapper.component.scss",
})
export class SwiperWrapperComponent {
  swiper!: Swiper;

  @Input() withPagination = true;
  @Input() withNavigation = true;

  pagesToShow: TemplateRef<unknown>[] = [];
  @Input() nested = false;

  @Input() backButtonEnabled = false;
  showBackButton = false;

  private _activeIndex$ = new BehaviorSubject<number>(0);
  private _pages$ = new Subject<Array<TemplateRef<unknown>>>();
  private _maxPageCount$ = new BehaviorSubject<number>(999);

  constructor(private _changeDetectorRef: ChangeDetectorRef) {
    combineLatest([this._maxPageCount$, this._pages$])
      .pipe(takeUntilDestroyed())
      .subscribe(([maxPageCount, pages]) => {
        if (pages?.length && this.pagesToShow.length !== maxPageCount) {
          this.pagesToShow = [...pages].splice(-maxPageCount);
          this.swiper?.destroy();
          this._changeDetectorRef.detectChanges();
          this._generateSwiper();
        }
      });

    combineLatest([this._activeIndex$, this._maxPageCount$])
      .pipe(takeUntilDestroyed(), debounceTime(1000))
      .subscribe(([activeIndex, maxPageCount]) => {
        this.showBackButton = activeIndex < maxPageCount - 1;
        this._changeDetectorRef.detectChanges();
      });
  }

  @Output() pageChanged = new EventEmitter<number>();
  @Input() whiteControls: boolean | null = false;
  @Input() pictureBackground: boolean = false;

  @HostBinding("class.picture-background") get isPictureBackground(): boolean {
    return this.pictureBackground;
  }

  @Input() className = '';

  @Input() set pages(value: TemplateRef<unknown>[]) {
    this._pages$.next(value);
  }

  @Input() set maxPageCount(value: number) {
    this._maxPageCount$.next(value);
  }

  private _currentPage: number | null = null;
  @Input() set currentPage(value: number) {
    this._currentPage = value;
    const swiper = this._getSwiper()
    swiper?.slideTo(value);
  }

  private _generateSwiper(): void {
    this.swiper = new Swiper(".swiper", {
      modules: [Navigation, Pagination],
      initialSlide: this._getInitialSlide(),
      slidesPerView: 1,
      speed: 400,
      spaceBetween: 100,
      nested: this.nested,
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      pagination: {
        el: ".swiper-pagination",
        type: "bullets",
      },
    });

    const swiper = this._getSwiper()
    swiper?.on("slideChange", (swiper: Swiper): void => {
      this._updateActiveIndex(swiper.activeIndex);
      this.pageChanged.emit(swiper.activeIndex);
      if (swiper.activeIndex === swiper.slides.length - 1) {
        this.showBackButton = false;
        this._changeDetectorRef.detectChanges();
      }
    });
  }



  private _getSwiper(): Swiper | null {
    if (!this.swiper) {
      return null
    }
    // @ts-ignore
    if (this.swiper["length"]) {
      // @ts-ignore
      return this.swiper[0];
    } else {
      return this.swiper;
    }
  }

  private _getInitialSlide(): number {
    let initialSlide: number;
    if (this._currentPage) {
      initialSlide = this._currentPage;
    } else if (this._activeIndex$.value < this.pagesToShow.length - 2) {
      initialSlide = this._activeIndex$.value;
    } else {
      initialSlide = this.pagesToShow.length - 1;
    }

    this._updateActiveIndex(initialSlide);
    return initialSlide;
  }

  private _updateActiveIndex(activeIndex: number): void {
    this._activeIndex$.next(activeIndex);
  }

  goToLastSlide(): void {
    const swiper = this._getSwiper()

    if (!swiper) {
      return;
    }
    const lastSlide = swiper.slides.length - 1;
    this._activeIndex$.next(lastSlide);
    swiper.slideTo(lastSlide);
    this.showBackButton = false;
  }
}
